import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Upload, Trash2, Loader2, ExternalLink, Search, Image as ImageIcon } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { BUILDS } from "@/data/builds";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * AdminBuilds — Upload portal for vehicle gallery images.
 * Admin selects a build, drags/uploads up to 25 MB images per file, edits labels & order.
 */
export default function AdminBuilds() {
    const { user, loading } = useAuth();
    const [selectedSlug, setSelectedSlug] = useState(BUILDS[0]?.slug || "");
    const [query, setQuery] = useState("");
    const [images, setImages] = useState([]);
    const [fetching, setFetching] = useState(false);
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return BUILDS;
        return BUILDS.filter(
            (b) =>
                b.name.toLowerCase().includes(q) ||
                b.slug.toLowerCase().includes(q) ||
                (b.manufacturer || "").toLowerCase().includes(q)
        );
    }, [query]);

    const selectedBuild = useMemo(
        () => BUILDS.find((b) => b.slug === selectedSlug),
        [selectedSlug]
    );

    async function loadImages(slug) {
        if (!slug) return;
        setFetching(true);
        try {
            const { data } = await api.get(`/builds/${slug}/images`);
            setImages(Array.isArray(data) ? data : []);
        } catch (e) {
            toast.error("Failed to load images");
        } finally {
            setFetching(false);
        }
    }

    useEffect(() => {
        let cancelled = false;
        async function load() {
            if (!selectedSlug) return;
            setFetching(true);
            try {
                const { data } = await api.get(`/builds/${selectedSlug}/images`);
                if (!cancelled) setImages(Array.isArray(data) ? data : []);
            } catch (_e) {
                if (!cancelled) toast.error("Failed to load images");
            } finally {
                if (!cancelled) setFetching(false);
            }
        }
        load();
        return () => { cancelled = true; };
    }, [selectedSlug]);

    async function handleFiles(files) {
        if (!selectedSlug) return;
        const list = Array.from(files || []);
        if (list.length === 0) return;
        setUploading(true);
        let success = 0;
        let failed = 0;
        for (const file of list) {
            try {
                const form = new FormData();
                form.append("file", file);
                form.append("label", "");
                form.append("order", String(images.length + success + 1));
                await api.post(`/admin/builds/${selectedSlug}/images`, form, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                success += 1;
            } catch (e) {
                failed += 1;
                console.error("upload failed", e);
            }
        }
        setUploading(false);
        if (success > 0) toast.success(`${success} image${success === 1 ? "" : "s"} uploaded`);
        if (failed > 0) toast.error(`${failed} upload${failed === 1 ? "" : "s"} failed`);
        await loadImages(selectedSlug);
    }

    async function handleDelete(imageId) {
        if (!window.confirm("Delete this image?")) return;
        try {
            await api.delete(`/admin/builds/${selectedSlug}/images/${imageId}`);
            toast.success("Deleted");
            setImages((prev) => prev.filter((i) => i.id !== imageId));
        } catch (e) {
            toast.error("Failed to delete");
        }
    }

    async function handleLabelBlur(image, newLabel) {
        if ((image.label || "") === (newLabel || "")) return;
        try {
            await api.patch(`/admin/builds/${selectedSlug}/images/${image.id}`, {
                label: newLabel,
            });
            setImages((prev) =>
                prev.map((i) => (i.id === image.id ? { ...i, label: newLabel } : i))
            );
        } catch (_e) {
            toast.error("Failed to save label");
        }
    }

    async function handleOrderChange(image, newOrder) {
        const n = Number(newOrder);
        if (!Number.isFinite(n) || n === image.order) return;
        try {
            await api.patch(`/admin/builds/${selectedSlug}/images/${image.id}`, {
                order: n,
            });
            setImages((prev) =>
                [...prev.map((i) => (i.id === image.id ? { ...i, order: n } : i))].sort(
                    (a, b) => (a.order || 0) - (b.order || 0)
                )
            );
        } catch (_e) {
            toast.error("Failed to save order");
        }
    }

    // ----- Render gates -----

    if (loading || user === null) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[var(--crewz-bg)] text-zinc-500 font-body">
                <Loader2 size={20} className="animate-spin" />
            </div>
        );
    }

    if (!user || user.role !== "admin") {
        return (
            <div className="min-h-screen bg-[var(--crewz-bg)] text-[var(--crewz-text)]">
                <Navbar />
                <section className="px-6 md:px-12 py-32 max-w-3xl mx-auto text-center">
                    <p className="font-body text-xs tracking-[0.3em] uppercase text-[var(--crewz-gold)] mb-6">
                        Members Only
                    </p>
                    <h1 className="font-display text-5xl md:text-6xl leading-[0.95] mb-6">
                        Admin Access Required.
                    </h1>
                    <p className="text-[var(--crewz-text-muted)] mb-8">
                        This portal is reserved for CrewZNatioN editors. Sign in with an admin account to continue.
                    </p>
                    <Link
                        to="/login"
                        state={{ from: "/admin/builds" }}
                        className="inline-flex items-center gap-2 bg-[var(--crewz-gold)] text-[var(--crewz-bg)] hover:bg-[var(--crewz-gold-soft)] px-7 py-4 text-sm tracking-[0.22em] uppercase font-bold transition-colors duration-300"
                        data-testid="admin-builds-login-cta"
                    >
                        Sign In
                        <ArrowUpRight size={14} />
                    </Link>
                </section>
                <Footer />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[var(--crewz-bg)] text-[var(--crewz-text)]" data-testid="admin-builds-page">
            <Navbar />

            <section className="px-6 md:px-12 lg:px-20 pt-32 md:pt-40 pb-12 max-w-7xl mx-auto">
                <Link
                    to="/"
                    className="inline-flex items-center gap-2 text-xs tracking-[0.22em] uppercase text-[var(--crewz-text-muted)] hover:text-[var(--crewz-gold)] transition-colors mb-8"
                >
                    <ArrowLeft size={14} />
                    Back
                </Link>
                <p className="font-body text-xs tracking-[0.3em] uppercase text-[var(--crewz-gold)] mb-4">
                    Admin · Build Gallery Editor
                </p>
                <h1 className="font-display text-5xl md:text-6xl lg:text-7xl leading-[0.9] tracking-[-0.02em] mb-4 max-w-4xl">
                    Curate The Garage.
                </h1>
                <p className="text-base md:text-lg text-[var(--crewz-text-muted)] max-w-2xl leading-relaxed">
                    Pick a vehicle, drag in your magazine-quality photos, label each angle. The first image (lowest order #) becomes the hero. Up to 25 MB per file.
                </p>
            </section>

            <section className="px-6 md:px-12 lg:px-20 pb-32 max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
                {/* SIDEBAR — build picker */}
                <aside className="lg:col-span-4 lg:sticky lg:top-28 self-start">
                    <div className="relative mb-4">
                        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--crewz-text-muted)]" />
                        <input
                            type="text"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Search vehicles…"
                            className="w-full bg-[var(--crewz-bg-2)] border border-white/10 focus:border-[var(--crewz-gold)] outline-none text-sm text-[var(--crewz-text)] placeholder:text-[var(--crewz-text-muted)] py-3 pl-9 pr-3 transition-colors"
                            data-testid="admin-builds-search"
                        />
                    </div>
                    <div className="max-h-[60vh] overflow-y-auto border border-white/10 bg-[var(--crewz-bg-2)]/40">
                        {filtered.map((b) => {
                            const active = b.slug === selectedSlug;
                            return (
                                <button
                                    key={b.slug}
                                    type="button"
                                    onClick={() => setSelectedSlug(b.slug)}
                                    className={`w-full text-left px-4 py-3 border-b border-white/5 transition-colors ${
                                        active
                                            ? "bg-[var(--crewz-gold)]/15 text-[var(--crewz-gold)]"
                                            : "hover:bg-white/[0.03] text-[var(--crewz-text)]"
                                    }`}
                                    data-testid={`admin-builds-select-${b.slug}`}
                                >
                                    <div className="text-sm leading-tight">{b.name}</div>
                                    <div className="text-[10px] tracking-[0.18em] uppercase text-[var(--crewz-text-muted)] mt-1">
                                        {b.manufacturer} · {b.type}
                                    </div>
                                </button>
                            );
                        })}
                        {filtered.length === 0 && (
                            <div className="px-4 py-6 text-sm text-[var(--crewz-text-muted)]">
                                No vehicles match.
                            </div>
                        )}
                    </div>
                </aside>

                {/* MAIN — selected build images + upload */}
                <div className="lg:col-span-8">
                    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
                        <div>
                            <p className="text-[10px] tracking-[0.3em] uppercase text-[var(--crewz-text-muted)] mb-2">
                                Editing
                            </p>
                            <h2 className="font-display text-3xl md:text-4xl tracking-[-0.01em] leading-tight">
                                {selectedBuild?.name || "—"}
                            </h2>
                            <Link
                                to={`/builds/${selectedSlug}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="mt-1 inline-flex items-center gap-1.5 text-xs tracking-[0.18em] uppercase text-[var(--crewz-text-muted)] hover:text-[var(--crewz-gold)]"
                                data-testid="admin-builds-preview-link"
                            >
                                Open Live Page <ExternalLink size={11} />
                            </Link>
                        </div>
                        <div className="text-xs tracking-[0.22em] uppercase text-[var(--crewz-text-muted)]">
                            {images.length} Image{images.length === 1 ? "" : "s"}
                        </div>
                    </div>

                    {/* Drop zone */}
                    <DropZone
                        disabled={uploading}
                        onFiles={handleFiles}
                        onPick={() => fileInputRef.current?.click()}
                    />
                    <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        accept="image/*,.heic,.heif"
                        className="hidden"
                        onChange={(e) => {
                            handleFiles(e.target.files);
                            e.target.value = "";
                        }}
                        data-testid="admin-builds-file-input"
                    />

                    {uploading && (
                        <div className="mt-4 inline-flex items-center gap-2 text-sm text-[var(--crewz-gold)]">
                            <Loader2 size={14} className="animate-spin" />
                            Uploading…
                        </div>
                    )}

                    {/* Images list */}
                    <div className="mt-10">
                        {fetching ? (
                            <div className="py-16 flex items-center justify-center text-[var(--crewz-text-muted)]">
                                <Loader2 size={18} className="animate-spin" />
                            </div>
                        ) : images.length === 0 ? (
                            <div className="py-20 border border-dashed border-white/10 text-center text-[var(--crewz-text-muted)]">
                                <ImageIcon size={28} className="mx-auto mb-3 opacity-50" strokeWidth={1.3} />
                                <p className="text-sm">No images uploaded yet. The live page will use the default gallery.</p>
                            </div>
                        ) : (
                            <ul className="space-y-4">
                                {images.map((img) => {
                                    const url = img.url.startsWith("http") ? img.url : `${BACKEND_URL}${img.url}`;
                                    return (
                                        <li
                                            key={img.id}
                                            className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6 items-start border border-white/10 bg-[var(--crewz-bg-2)]/40 p-4"
                                            data-testid={`admin-builds-image-${img.id}`}
                                        >
                                            <div className="md:col-span-4 aspect-[3/2] bg-[var(--crewz-bg-3)] flex items-center justify-center overflow-hidden">
                                                <img
                                                    src={url}
                                                    alt={img.label || "Build image"}
                                                    className="max-w-full max-h-full object-contain"
                                                    loading="lazy"
                                                />
                                            </div>
                                            <div className="md:col-span-8 space-y-3">
                                                <div>
                                                    <label className="text-[10px] tracking-[0.3em] uppercase text-[var(--crewz-text-muted)]">
                                                        Label
                                                    </label>
                                                    <input
                                                        type="text"
                                                        defaultValue={img.label || ""}
                                                        placeholder="e.g. Engine Bay, Front 3/4, Interior"
                                                        onBlur={(e) => handleLabelBlur(img, e.target.value)}
                                                        className="mt-1 w-full bg-transparent border-b border-white/15 focus:border-[var(--crewz-gold)] outline-none text-base text-[var(--crewz-text)] py-1.5"
                                                        data-testid={`admin-builds-label-${img.id}`}
                                                    />
                                                </div>
                                                <div className="flex flex-wrap items-end gap-6">
                                                    <div>
                                                        <label className="text-[10px] tracking-[0.3em] uppercase text-[var(--crewz-text-muted)]">
                                                            Order
                                                        </label>
                                                        <input
                                                            type="number"
                                                            defaultValue={img.order ?? 0}
                                                            onBlur={(e) => handleOrderChange(img, e.target.value)}
                                                            className="mt-1 w-24 bg-transparent border-b border-white/15 focus:border-[var(--crewz-gold)] outline-none text-base text-[var(--crewz-text)] py-1.5"
                                                            data-testid={`admin-builds-order-${img.id}`}
                                                        />
                                                    </div>
                                                    <div className="text-[10px] tracking-[0.18em] uppercase text-[var(--crewz-text-muted)]">
                                                        {Math.round((img.size || 0) / 1024)} KB
                                                    </div>
                                                </div>
                                                <div className="pt-2 flex items-center gap-3">
                                                    <a
                                                        href={url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="inline-flex items-center gap-1.5 text-xs tracking-[0.22em] uppercase text-[var(--crewz-text-muted)] hover:text-[var(--crewz-gold)]"
                                                    >
                                                        Open <ExternalLink size={11} />
                                                    </a>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleDelete(img.id)}
                                                        className="inline-flex items-center gap-1.5 text-xs tracking-[0.22em] uppercase text-red-400 hover:text-red-300"
                                                        data-testid={`admin-builds-delete-${img.id}`}
                                                    >
                                                        <Trash2 size={12} />
                                                        Delete
                                                    </button>
                                                </div>
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
}

function DropZone({ onFiles, onPick, disabled }) {
    const [over, setOver] = useState(false);
    return (
        <div
            onClick={onPick}
            onDragOver={(e) => {
                e.preventDefault();
                setOver(true);
            }}
            onDragLeave={() => setOver(false)}
            onDrop={(e) => {
                e.preventDefault();
                setOver(false);
                if (disabled) return;
                onFiles(e.dataTransfer.files);
            }}
            className={`cursor-pointer border-2 border-dashed transition-colors p-10 md:p-14 text-center ${
                over
                    ? "border-[var(--crewz-gold)] bg-[var(--crewz-gold)]/5"
                    : "border-white/15 hover:border-[var(--crewz-gold)]/60"
            } ${disabled ? "opacity-50 pointer-events-none" : ""}`}
            data-testid="admin-builds-dropzone"
        >
            <Upload size={28} className="mx-auto mb-4 text-[var(--crewz-gold)]" strokeWidth={1.4} />
            <p className="font-display text-xl md:text-2xl tracking-[-0.01em]">
                Drop Photos Here
            </p>
            <p className="text-sm text-[var(--crewz-text-muted)] mt-2">
                or click to browse · JPG / PNG / WebP / HEIC · 25 MB max
            </p>
        </div>
    );
}
